import { createClient } from '@supabase/supabase-js';
import { supabase as mainClient, supabaseUrl, supabaseAnonKey } from './supabase';
import { logger, logProfileOperation, withPerformanceLogging } from './structuredLogger';

/**
 * Ephemeral Supabase client for owner creation
 * This client has session persistence disabled to prevent overwriting the manager's session
 */
const createEphemeralClient = () => {
  return createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
    },
  });
};

/**
 * Create an owner user account without affecting the current manager session
 * @param email - Owner email
 * @param password - Owner password
 * @param metadata - User metadata
 * @returns Promise with success/error result
 */
export const createOwnerAccount = async (
  email: string,
  password: string,
  metadata: {
    first_name: string;
    last_name: string;
    phone?: string;
    address?: string;
    city?: string;
    country?: string;
    nationality?: string;
    id_number?: string;
    is_foreign?: boolean;
  }
) => {
  const startTime = Date.now();

  try {
    logger.info('owner', 'account_creation_start', `Starting owner account creation for ${email}`, {
      email,
      hasPhone: !!metadata.phone,
    });

    // Route through Edge Function (service role) for guaranteed creation
    const edgeUrl = `${supabaseUrl}/functions/v1/create-owner`;
    let json: any = null;
    let fnErrorMsg: string | undefined;

    const requestBody = {
      email,
      password,
      first_name: metadata.first_name,
      last_name: metadata.last_name,
      phone: metadata.phone,
      address: metadata.address,
      city: metadata.city,
      country: metadata.country,
      nationality: metadata.nationality,
      id_number: metadata.id_number,
      is_foreign: metadata.is_foreign,
    };

    // Try using Supabase functions.invoke first
    try {
      const { data: fnData, error: fnError } = await mainClient.functions.invoke('create-owner', {
        body: requestBody,
      });
      json = fnData as any;
      fnErrorMsg = fnError?.message;
      console.log('🔍 Edge Function response:', { fnData, fnError, json });
    } catch (e: any) {
      fnErrorMsg = e?.message || 'Edge function call failed';
      console.log('🔍 Edge Function error (functions.invoke):', e);

      // Fallback: Try direct fetch if functions.invoke fails (e.g., on localhost with CORS issues)
      try {
        console.log('🔄 Attempting direct fetch fallback to:', edgeUrl);
        const response = await fetch(edgeUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseAnonKey}`,
            'apikey': supabaseAnonKey,
          },
          body: JSON.stringify(requestBody),
        });

        if (response.ok) {
          json = await response.json();
          fnErrorMsg = undefined; // Clear the error since fallback succeeded
          console.log('✅ Direct fetch fallback succeeded:', json);
        } else {
          const errorText = await response.text();
          console.log('❌ Direct fetch fallback failed:', response.status, errorText);
          try {
            const errorJson = JSON.parse(errorText);
            fnErrorMsg = errorJson.error || `HTTP ${response.status}`;
          } catch {
            fnErrorMsg = `HTTP ${response.status}: ${errorText}`;
          }
        }
      } catch (fetchError: any) {
        console.log('❌ Direct fetch fallback error:', fetchError);
        // Keep original error message if fallback also fails
      }
    }
    if (fnErrorMsg || !json?.userId) {
      const msg = json?.error || fnErrorMsg || 'Failed creating user via edge function';
      logger.error('owner', 'account_creation_failed', msg, { email });
      return { success: false, error: msg, userId: null };
    }

    const userId = json.userId as string;

    const duration = Date.now() - startTime;
    logger.info('owner', 'account_created', `Owner account created successfully for ${email}`, {
      userId,
      email,
      duration,
    });

    return { success: true, error: null, userId };
  } catch (error: any) {
    const duration = Date.now() - startTime;
    logger.error('owner', 'account_creation_error', error.message, {
      email,
      error: error.message,
      stack: error.stack,
      duration,
    });
    return { success: false, error: error.message || 'Unexpected error', userId: null };
  }
};

/**
 * Create owner profile in database
 * @param userId - User ID from auth signup
 * @param profileData - Profile data to insert
 * @returns Promise with success/error result
 */
export const createOwnerProfile = async (
  userId: string,
  profileData: {
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    address?: string;
    city?: string;
    country?: string;
    nationality?: string;
    id_number?: string;
    is_foreign?: boolean;
  }
) => {
  const startTime = Date.now();

  try {
    logger.info('profile', 'creation_start', `Starting profile creation for owner ${userId}`, {
      userId,
      email: profileData.email,
      hasAddress: !!profileData.address,
    });

    // Use main authenticated client so RLS policies for managers/admins apply
    // Use upsert to handle cases where edge function already created the profile
    // Owners created by manager are always approved
    const { data, error } = await mainClient
      .from('profiles')
      .upsert({
        id: userId,
        email: profileData.email,
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        phone: profileData.phone || null,           // Legacy column
        phone_number: profileData.phone || null,    // Main phone column - write to both
        role: 'owner',
        status: 'active',                           // Always active
        approval_status: 'approved',                // Always approved (no admin approval needed)
        profile_type: 'owner',
        address: profileData.address || null,
        city: profileData.city || null,
        country: profileData.country || null,
        nationality: profileData.nationality || null,
        id_number: profileData.id_number || null,
        is_foreign: profileData.is_foreign || false,
        can_bid: true,
      }, {
        onConflict: 'id',
      })
      .select()
      .single();

    if (error) {
      const duration = Date.now() - startTime;
      logProfileOperation(userId, 'create', false, {
        error: error.message,
        code: error.code,
        duration,
      });
      return { success: false, error: error.message, profile: null };
    }

    const duration = Date.now() - startTime;
    logProfileOperation(userId, 'create', true, {
      profileId: data.id,
      duration,
    });

    logger.info('profile', 'created', `Owner profile created successfully for user ${userId}`, {
      userId,
      profileId: data.id,
      duration,
    });

    return { success: true, error: null, profile: data };
  } catch (error: any) {
    const duration = Date.now() - startTime;
    logProfileOperation(userId, 'create', false, {
      error: error.message,
      stack: error.stack,
      duration,
    });
    return { success: false, error: error.message || 'Unexpected error', profile: null };
  }
};

/**
 * Complete owner creation process (account + profile)
 * @param ownerData - Complete owner data
 * @returns Promise with success/error result
 */
export const createOwnerComplete = async (ownerData: {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone?: string;
  address?: string;
  city?: string;
  country?: string;
  nationality?: string;
  id_number?: string;
  is_foreign?: boolean;
}) => {
  const startTime = Date.now();

  try {
    logger.info('owner', 'complete_creation_start', `Starting complete owner creation for ${ownerData.email}`, {
      email: ownerData.email,
      hasPhone: !!ownerData.phone,
      hasAddress: !!ownerData.address,
    });

    // Step 1: Create auth account
    const authResult = await createOwnerAccount(
      ownerData.email,
      ownerData.password,
      {
        first_name: ownerData.first_name,
        last_name: ownerData.last_name,
        phone: ownerData.phone,
        address: ownerData.address,
        city: ownerData.city,
        country: ownerData.country,
        nationality: ownerData.nationality,
        id_number: ownerData.id_number,
        is_foreign: ownerData.is_foreign,
      }
    );

    if (!authResult.success) {
      const duration = Date.now() - startTime;
      logger.error('owner', 'complete_creation_failed', `Auth account creation failed for ${ownerData.email}`, {
        email: ownerData.email,
        error: authResult.error,
        duration,
      });
      return { success: false, error: authResult.error, userId: null, profile: null };
    }

    // Step 2: Create profile (edge function likely already created it, but upsert handles that)
    const profileResult = await createOwnerProfile(authResult.userId!, {
      first_name: ownerData.first_name,
      last_name: ownerData.last_name,
      email: ownerData.email,
      phone: ownerData.phone,
      address: ownerData.address,
      city: ownerData.city,
      country: ownerData.country,
      nationality: ownerData.nationality,
      id_number: ownerData.id_number,
      is_foreign: ownerData.is_foreign,
    });

    if (!profileResult.success) {
      // Note: Auth account was created but profile failed
      const duration = Date.now() - startTime;
      logger.warn('owner', 'profile_creation_failed', `Profile creation failed for ${ownerData.email}, auth account exists`, {
        email: ownerData.email,
        userId: authResult.userId,
        profileError: profileResult.error,
        duration,
      });
      return {
        success: false,
        error: `Account created but profile failed: ${profileResult.error}`,
        userId: authResult.userId,
        profile: null
      };
    }

    const duration = Date.now() - startTime;
    logger.info('owner', 'complete_creation_success', `Complete owner creation successful for ${ownerData.email}`, {
      email: ownerData.email,
      userId: authResult.userId,
      profileId: profileResult.profile?.id,
      duration,
    });

    return {
      success: true,
      error: null,
      userId: authResult.userId,
      profile: profileResult.profile
    };
  } catch (error: any) {
    const duration = Date.now() - startTime;
    logger.error('owner', 'complete_creation_error', `Unexpected error in complete owner creation for ${ownerData.email}`, {
      email: ownerData.email,
      error: error.message,
      stack: error.stack,
      duration,
    });
    return {
      success: false,
      error: error.message || 'Unexpected error',
      userId: null,
      profile: null
    };
  }
};
